/**
 * useSectionLoader — Lazy section loading via TanStack Query.
 * Phase 3B: replaces loadSectionData in useEditorData.
 *
 * Fetches section model data on demand and merges into the scenario-tree cache.
 * Query key: ['section-data', scenarioId, sectionId]
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getSectionData, getSensitivity } from "../../api/scenarios";
import {
  mapModelDataList,
  mapField,
  copyFields,
} from "../../../features/financial-editor/services/dataMappers";
import { SCENARIO_TREE_KEY, type EditorState, createInitialState } from "./useScenarioTree";

export const SECTION_DATA_KEY = "section-data";

interface UseSectionLoaderParams {
  scenarioId: number;
  currentSensitivity: any;
}

export function useSectionLoader({
  scenarioId,
  currentSensitivity,
}: UseSectionLoaderParams) {
  const queryClient = useQueryClient();
  const loadedSectionsRef = useRef<Set<string>>(new Set());
  const currentSensitivityRef = useRef(currentSensitivity);
  currentSensitivityRef.current = currentSensitivity;
  const [sectionDataLoading, setSectionDataLoading] = useState<string | null>(null);

  // Reset loaded sections tracking when scenario or sensitivity changes
  useEffect(() => {
    loadedSectionsRef.current = new Set();
  }, [scenarioId, currentSensitivity?.id]);

  const loadSection = useCallback(
    async (sectionId: string, forceReload: boolean = false) => {
      if (!forceReload && loadedSectionsRef.current.has(sectionId)) {
        return;
      }

      const isInitialLoad = !forceReload && !loadedSectionsRef.current.has(sectionId);
      if (isInitialLoad) {
        setSectionDataLoading(sectionId);
      }

      try {
        const responseData = await queryClient.fetchQuery({
          queryKey: [SECTION_DATA_KEY, scenarioId, sectionId],
          queryFn: () => getSectionData(scenarioId, sectionId),
          staleTime: forceReload ? 0 : 30_000,
        });

        const sectionInputItems = mapModelDataList(
          (responseData as any)["input"] || []
        );
        const sectionOutputItems = mapModelDataList(
          (responseData as any)["output"] || []
        );

        let sensitivityData: any = null;
        const latestSensitivity = currentSensitivityRef.current;
        if (latestSensitivity && latestSensitivity !== "null") {
          try {
            sensitivityData = await getSensitivity(latestSensitivity.id);
          } catch (err) {
            console.error("Failed to fetch sensitivity for section load:", err);
          }
        }

        queryClient.setQueryData<EditorState>(
          [SCENARIO_TREE_KEY, scenarioId],
          (old) => {
            const prev = old ?? createInitialState();

            const newInputItems = {
              ...prev.Input.tree.items,
              ...sectionInputItems,
            };
            const newOutputItems = {
              ...prev.Output.tree.items,
              ...sectionOutputItems,
            };

            if (sensitivityData) {
              Object.keys(newInputItems).forEach((key) => {
                newInputItems[key] = {
                  ...newInputItems[key],
                  sensitivityModelData: null,
                };
              });
              sensitivityData.model_datas?.forEach((sObj: any) => {
                const item = newInputItems[sObj.original_model_data_id];
                if (item) {
                  newInputItems[sObj.original_model_data_id] = {
                    ...item,
                    sensitivityModelData: sObj,
                  };
                }
              });
              sensitivityData.output?.forEach((obj: any) => {
                const currentObj = newOutputItems[obj.id.toString()];
                if (currentObj) {
                  const patched = { ...currentObj };
                  copyFields(obj, patched);
                  newOutputItems[obj.id.toString()] = patched;
                }
              });
            }

            const sectionInputRootId = Object.keys(sectionInputItems).find(
              (id) => sectionInputItems[id].type === "input-root"
            );
            const inputRootId =
              sectionInputRootId || prev.Input.tree.rootId;

            const sectionOutputRootId = Object.keys(sectionOutputItems).find(
              (id) => sectionOutputItems[id].type === "output-root"
            );
            const outputRootId =
              sectionOutputRootId || prev.Output.tree.rootId;

            return {
              ...prev,
              Input: {
                tree: { items: newInputItems, rootId: inputRootId },
                field_values: {
                  ...prev.Input.field_values,
                  ...mapField(sectionInputItems),
                },
              },
              Output: {
                tree: { items: newOutputItems, rootId: outputRootId },
                field_values: {
                  ...prev.Output.field_values,
                  ...mapField(sectionOutputItems),
                },
              },
            };
          }
        );

        loadedSectionsRef.current = new Set([
          ...loadedSectionsRef.current,
          sectionId,
        ]);
      } catch (error) {
        console.error(`Error loading section data for ${sectionId}:`, error);
      }

      if (isInitialLoad) {
        setSectionDataLoading(null);
      }
    },
    [scenarioId, queryClient]
  );

  const resetLoadedSections = useCallback(() => {
    loadedSectionsRef.current = new Set();
    setSectionDataLoading(null);
  }, []);

  const invalidateSection = useCallback(
    (sectionId: string) => {
      queryClient.invalidateQueries({
        queryKey: [SECTION_DATA_KEY, scenarioId, sectionId],
      });
      loadedSectionsRef.current.delete(sectionId);
    },
    [scenarioId, queryClient]
  );

  const invalidateAllSections = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [SECTION_DATA_KEY, scenarioId],
    });
    loadedSectionsRef.current = new Set();
  }, [scenarioId, queryClient]);

  return {
    loadSection,
    resetLoadedSections,
    invalidateSection,
    invalidateAllSections,
    sectionDataLoading,
  };
}
